import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { userService } from "@/api/services"
import { useAuth } from "@/contexts/AuthContext"
import type { UserResponseDto, UserListParams, UserType } from "@/api/types/user.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
// Using native select for simplicity
import { Loader2, Search, ChevronLeft, ChevronRight, Edit2, X } from "lucide-react"

export default function UsersListPage() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<UserResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  
  // Filters and pagination
  const [params, setParams] = useState<UserListParams>({
    page: 1,
    limit: 50,
  })
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Filter states
  const [search, setSearch] = useState("")
  const [userType, setUserType] = useState<UserType | "">("")
  const [isActive, setIsActive] = useState<string>("")

  // Debounce function
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      return () => {
        clearTimeout(handler)
      }
    }, [value, delay])

    return debouncedValue
  }

  // Debounced search value
  const debouncedSearch = useDebounce(search, 500)

  // Auto-apply filters when they change
  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      page: 1,
      search: debouncedSearch || undefined,
      userType: userType !== "" ? userType : undefined,
      isActive: isActive === "" ? undefined : isActive === "true",
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, userType, isActive])

  useEffect(() => {
    loadUsers()
  }, [params])

  const loadUsers = async () => {
    setLoading(true)
    setError("")
    try {
      const response = await userService.getUsers(params)
      // Filter out the current logged-in user from the list
      const filteredUsers = currentUser
        ? response.data.filter((user) => user.id !== currentUser.id)
        : response.data
      
      setUsers(filteredUsers)
      // Adjust total count if we filtered out the current user
      const adjustedTotal = currentUser && response.data.some((u) => u.id === currentUser.id)
        ? response.total - 1
        : response.total
      setTotal(adjustedTotal)
      // Sync currentPage with response, but only if it matches what we requested
      const responsePage = response.page || params.page || 1
      setCurrentPage(responsePage)
      setTotalPages(response.totalPages || Math.ceil(adjustedTotal / (params.limit || 50)))
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load users"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    setSearch("")
    setUserType("")
    setIsActive("")
    // Reset will trigger via useEffect
  }

  const handleSearch = () => {
    // Manual search trigger (for Enter key or button click)
    // The debounced search will handle it automatically
    setParams({
      ...params,
      page: 1,
      search: search || undefined,
      userType: userType !== "" ? userType : undefined,
      isActive: isActive === "" ? undefined : isActive === "true",
    })
  }

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setCurrentPage(newPage)
    setParams((prevParams) => ({ ...prevParams, page: newPage }))
  }

  const getUserName = (user: UserResponseDto) => {
    return `${user.firstName} ${user.lastName}`.trim()
  }

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <h1 className="text-3xl font-bold text-foreground mb-2">Users Management</h1>
          <p className="text-muted-foreground mb-8">Manage all users in the system</p>

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Input
                    id="search"
                    placeholder="Email or name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pr-8"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {search && (
                  <p className="text-xs text-muted-foreground">
                    Searching automatically as you type...
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="userType">User Type</Label>
                <select
                  id="userType"
                  value={userType}
                  onChange={(e) => {
                    const value = e.target.value
                    setUserType(value === "" ? "" : (value as UserType))
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All types</option>
                  <option value="Basic">Basic</option>
                  <option value="Company">Company</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="isActive">Status</Label>
                <select
                  id="isActive"
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All statuses</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                {(search || userType || isActive) && (
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    className="flex-1"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Clear
                  </Button>
                )}
                <Button onClick={handleSearch} className="flex-1">
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </div>
            </div>
          </Card>

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Users Table */}
          <Card>
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : users.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                No users found
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow
                          key={user.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          <TableCell className="font-medium">
                            {getUserName(user)}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                          <TableCell>{user.city}</TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                user.userType === "Admin"
                                  ? "bg-primary/10 text-primary"
                                  : user.userType === "Company"
                                  ? "bg-success/10 text-success"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {user.userType}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                user.isActive
                                  ? "bg-success/10 text-success"
                                  : "bg-destructive/10 text-destructive"
                              }`}
                            >
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/users/${user.id}`)
                              }}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * (params.limit || 50)) + 1} to{" "}
                      {Math.min(currentPage * (params.limit || 50), total)} of {total} users
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <div className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </main>
    </div>
  )
}

